import { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown, Download, Copy } from 'lucide-react'
import { ReportDataRow, ReportBreakdown } from '../../../types/reports'
import {
  DERIVED_METRICS,
  formatMetricLabel,
  getMetricValue,
} from '../metricsConfig'

interface Props {
  data: ReportDataRow[]
  breakdowns: ReportBreakdown[]
  isLoading: boolean
  timezone?: string
  selectedMetrics: string[]
}

function getTimezoneAbbr(ianaTimezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: ianaTimezone,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    return parts.find(p => p.type === 'timeZoneName')?.value ?? ianaTimezone
  } catch {
    return ianaTimezone
  }
}

type SortDir = 'asc' | 'desc' | null

const PAGE_SIZE = 50

function fmtNum(n: number): string {
  return n.toLocaleString()
}

function fmtDate(d: string): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {})
}

interface ColDef {
  key: string
  label: string
  numeric?: boolean
  render: (row: ReportDataRow) => string | React.ReactNode
  csv: (row: ReportDataRow) => string
}

const breakdownColDefs: Record<ReportBreakdown, ColDef> = {
  campaigns: {
    key: 'campaign_id',
    label: 'Campaign',
    render: (row) => {
      const name = row.campaign_name ?? (row.campaign_id ? `ID ${row.campaign_id}` : '—')
      return (
        <span className="flex items-center gap-1 group">
          <span className="truncate max-w-[200px]" title={String(name)}>{String(name)}</span>
          <button
            onClick={() => copyText(String(name))}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            title="Copy"
          >
            <Copy className="w-3 h-3" />
          </button>
        </span>
      )
    },
    csv: (row) => String(row.campaign_name ?? (row.campaign_id ? `ID ${row.campaign_id}` : '')),
  },
  ad_units: {
    key: 'ad_unit_id',
    label: 'Ad Unit',
    render: (row) => {
      const name = row.ad_unit_name ?? (row.ad_unit_id ? `ID ${row.ad_unit_id}` : '—')
      return <span className="truncate max-w-[200px]" title={String(name)}>{String(name)}</span>
    },
    csv: (row) => String(row.ad_unit_name ?? (row.ad_unit_id ? `ID ${row.ad_unit_id}` : '')),
  },
  ads: {
    key: 'ad_id',
    label: 'Ad',
    render: (row) => String(row.ad_name ?? (row.ad_id ? `ID ${row.ad_id}` : '—')),
    csv: (row) => String(row.ad_name ?? (row.ad_id ? `ID ${row.ad_id}` : '')),
  },
  date: {
    key: 'date',
    label: 'Date',
    render: (row) => fmtDate(String(row.date ?? '')),
    csv: (row) => String(row.date ?? ''),
  },
  hour: {
    key: 'hour',
    label: 'Hour',
    render: (row) => row.hour !== undefined ? (
      <span className="tabular-nums">
        {String(row.hour).padStart(2, '0')}:00
      </span>
    ) : '—',
    csv: (row) => row.hour !== undefined ? `${String(row.hour).padStart(2, '0')}:00` : '',
  },
  country: {
    key: 'country',
    label: 'Country',
    render: (row) => {
      const name = row.country_name ?? (row.country !== undefined ? String(row.country) : '—')
      return String(name) !== '—' ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          {String(name)}
        </span>
      ) : <span>—</span>
    },
    csv: (row) => String(row.country_name ?? (row.country !== undefined ? String(row.country) : '')),
  },
  domain: {
    key: 'domain',
    label: 'Domain',
    render: (row) => {
      const name = row.domain_name ?? (row.domain !== undefined ? String(row.domain) : '—')
      return (
        <span className="flex items-center gap-1 group">
          <span className="truncate max-w-[220px]" title={String(name)}>{String(name)}</span>
          <button
            onClick={() => copyText(String(name))}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            title="Copy"
          >
            <Copy className="w-3 h-3" />
          </button>
        </span>
      )
    },
    csv: (row) => String(row.domain_name ?? (row.domain !== undefined ? String(row.domain) : '')),
  },
  content_type: {
    key: 'content_type',
    label: 'Content Type',
    render: (row) => String(row.content_type ?? '—'),
    csv: (row) => String(row.content_type ?? ''),
  },
  size: {
    key: 'size',
    label: 'Size',
    render: (row) => String(row.size ?? '—'),
    csv: (row) => String(row.size ?? ''),
  },
}

function getSortValue(row: ReportDataRow, colKey: string): string | number {
  return (row[colKey] as string | number) ?? ''
}

export function ReportTable({ data, breakdowns, isLoading, timezone, selectedMetrics }: Props) {
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)
  const [page, setPage] = useState(1)

  const tzAbbr = timezone ? getTimezoneAbbr(timezone) : null

  const dimCols = breakdowns
    .filter((b) => b in breakdownColDefs)
    .map((b) => {
      const col = { ...breakdownColDefs[b] }
      if (tzAbbr) {
        if (b === 'date') col.label = `Date (${tzAbbr})`
        if (b === 'hour') col.label = `Hour (${tzAbbr})`
      }
      return col
    })

  // Build metric column definitions from selectedMetrics
  const derivedKeys = new Set(DERIVED_METRICS.map((d) => d.key))

  const metricCols: ColDef[] = selectedMetrics.map((key) => {
    const derived = DERIVED_METRICS.find((d) => d.key === key)
    if (derived) {
      return {
        key,
        label: derived.label,
        numeric: true,
        render: (row) => {
          const imp = getMetricValue(row, 'impression')
          const clk = getMetricValue(row, 'click')
          return derived.formatValue(derived.compute({ impression: imp, click: clk, ...Object.fromEntries(Object.entries(row).map(([k, v]) => [k, Number(v) || 0])) }))
        },
        csv: (row) => {
          const vals = Object.fromEntries(Object.entries(row).map(([k, v]) => [k, Number(v) || 0]))
          return derived.formatValue(derived.compute(vals))
        },
      }
    }
    return {
      key,
      label: formatMetricLabel(key),
      numeric: true,
      render: (row) => fmtNum(getMetricValue(row, key)),
      csv: (row) => String(getMetricValue(row, key)),
    }
  })

  const allCols = [...dimCols, ...metricCols]

  const sorted = useMemo(() => {
    if (!sortCol || !sortDir) return data
    return [...data].sort((a, b) => {
      const av = getSortValue(a, sortCol)
      const bv = getSortValue(b, sortCol)
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av))
    })
  }, [data, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Compute column totals for the footer
  const rawTotals: Record<string, number> = {}
  for (const key of selectedMetrics) {
    if (!derivedKeys.has(key)) {
      rawTotals[key] = data.reduce((s, r) => s + getMetricValue(r, key), 0)
    }
  }

  function handleSort(key: string) {
    if (sortCol === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else if (sortDir === 'desc') { setSortCol(null); setSortDir(null) }
    } else {
      setSortCol(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  function handleExportCsv() {
    const headers = allCols.map((c) => c.label).join(',')
    const rows = data.map((row) =>
      allCols.map((c) => {
        const val = c.csv(row)
        return val.includes(',') ? `"${val}"` : val
      }).join(',')
    )
    const csv = [headers, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `report_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function SortIcon({ colKey }: { colKey: string }) {
    if (sortCol !== colKey) return <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
    if (sortDir === 'asc') return <ChevronUp className="w-3.5 h-3.5 text-primary-500" />
    return <ChevronDown className="w-3.5 h-3.5 text-primary-500" />
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-8 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50">
              {[1, 2, 3, 4].map((i) => (
                <th key={i} className="px-4 py-3">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                {[1, 2, 3, 4].map((j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
          <ChevronsUpDown className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 font-medium">No data for the selected filters</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {sorted.length.toLocaleString()} row{sorted.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={handleExportCsv}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-300 dark:border-gray-700"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50">
              {allCols.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors ${
                    col.numeric ? 'text-right' : 'text-left'
                  }`}
                >
                  <span className="flex items-center gap-1 justify-start">
                    {col.numeric && <span className="flex-1" />}
                    {col.label}
                    <SortIcon colKey={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {paginated.map((row, idx) => (
              <tr
                key={idx}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
              >
                {allCols.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-gray-800 dark:text-gray-200 ${
                      col.numeric ? 'text-right tabular-nums' : 'text-left'
                    }`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {dimCols.map((col, i) => (
                <td key={col.key} className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {i === 0 ? 'TOTAL' : ''}
                </td>
              ))}
              {metricCols.map((col) => {
                const derived = DERIVED_METRICS.find((d) => d.key === col.key)
                const cellValue = derived
                  ? derived.formatValue(derived.compute(rawTotals))
                  : fmtNum(rawTotals[col.key] ?? 0)
                return (
                  <td
                    key={col.key}
                    className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white tabular-nums"
                  >
                    {cellValue}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
